import {
  IconBuildingBank,
  IconHierarchy3,
  IconUsers,
  IconReceiptTax,
  IconPackage,
  IconChartLine,
  IconCoins,
  IconStairsDown,
  IconCoinPound,
  IconVip,
  IconTableImport,
  IconTableExport,
  IconGitPullRequest,
} from '@tabler/icons-react';

import { AppMenuItem, GuardedItem } from './interfaces';
import { FeatureFlag, filterMenuByGuard, Permission } from '@pmp/contexts';

const BROKER_ITEMS: GuardedItem[] = [
  {
    label: 'lender.lender.menuLabel',
    key: 'lender',
    icon: <IconBuildingBank size={20} strokeWidth={1.5} />,
    guard: { permissions: ['lenders:view'], flags: ['enableLender'] },
  },
  {
    label: 'firms.menuLabel',
    key: 'firms',
    icon: <IconHierarchy3 size={20} strokeWidth={1.5} />,
    guard: { permissions: ['firms:view'], flags: ['enableFirms'] },
  },
  {
    label: 'lender.brokers.menuLabel',
    key: 'brokers',
    icon: <IconUsers size={20} strokeWidth={1.5} />,
    guard: { permissions: ['brokers:view'], flags: ['enableBrokers'] },
  },
  {
    label: 'lender.procurationFees.menuLabel',
    key: 'procuration-fees',
    icon: <IconReceiptTax size={20} strokeWidth={1.5} />,
    guard: { permissions: ['procurationFees:view'], flags: ['enableProcurationFees'] },
  },
  {
    label: 'pmp.exclusiveProducts.menuLabel',
    key: 'exclusives',
    icon: <IconVip size={20} strokeWidth={1.5} />,
    guard: { permissions: ['exclusivitySets:view'], flags: ['enableFirms'] },
  },
];

const PRODUCT_ITEMS: GuardedItem[] = [
  {
    label: 'pmp.products.menuLabel',
    key: 'products',
    icon: <IconPackage size={20} strokeWidth={1.5} />,
    guard: { permissions: ['products:view'] },
  },
  {
    label: 'pmp.baseRates.menuLabel',
    key: 'rates',
    icon: <IconChartLine size={20} strokeWidth={1.5} />,
    guard: { permissions: ['baseRates:view'] },
  },
  {
    label: 'pmp.exchangeRates.menuLabel',
    key: 'exchange-rates',
    icon: <IconCoins size={20} strokeWidth={1.5} />,
    guard: { permissions: ['exchangeRates:view'], flags: ['enableExchangeRates'] },
  },
  {
    label: 'pmp.erc.menuLabel',
    key: 'erc',
    icon: <IconStairsDown size={20} strokeWidth={1.5} />,
    guard: { permissions: ['ercProfiles:view'] },
  },
  {
    label: 'pmp.fees.menuLabel',
    key: 'fees',
    icon: <IconCoinPound size={20} strokeWidth={1.5} />,
    guard: { permissions: ['fees:view'] },
  },
];

const getSharedItems = (isEnabledFourEyes: boolean): GuardedItem[] => [
  {
    label: 'global.menuLabels.imports',
    key: 'imports',
    icon: <IconTableImport size={20} strokeWidth={1.5} />,
    guard: { permissions: ['imports:view'], flags: ['enableImports'] },
  },
  {
    label: 'global.menuLabels.exports',
    key: 'exports',
    icon: <IconTableExport size={20} strokeWidth={1.5} />,
    guard: { permissions: ['exportTemplates:view'], flags: ['enableExports'] },
  },
  {
    label: isEnabledFourEyes ? 'global.menuLabels.changeRequests' : 'global.menuLabels.changeTracker',
    key: 'change-requests',
    icon: <IconGitPullRequest size={20} strokeWidth={1.5} />,
    guard: {
      permissions: ['changeRequests:view'],
    },
  },
];

export const getSideBarItems: (userPermissions: Set<Permission>, envFeatureFlags: Set<FeatureFlag>) => AppMenuItem[] = (
  userPermissions,
  envFeatureFlags,
) => {
  const items: GuardedItem[] = [
    ...BROKER_ITEMS,
    { type: 'divider' },
    ...PRODUCT_ITEMS,
    { type: 'divider' },
    ...getSharedItems(envFeatureFlags.has('enableFourEyes')),
  ];

  return filterMenuByGuard(items, userPermissions, envFeatureFlags);
};

/** Which of the two portals the account can reach: the broker side (BMP), the product side (PMP), or both. */
export const getPortalSections = (userPermissions: Set<Permission>, envFeatureFlags: Set<FeatureFlag>) => ({
  brokers: filterMenuByGuard(BROKER_ITEMS, userPermissions, envFeatureFlags).length > 0,
  products: filterMenuByGuard(PRODUCT_ITEMS, userPermissions, envFeatureFlags).length > 0,
});
